import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { Zap, AlertCircle, PartyPopper, Users, Award } from "lucide-react";
import { toast } from "sonner";
import PasswordInput from "@/components/PasswordInput";

/*
 * Iter309 — Role-aware sign-in copy. Reads `?as=coach|player|organiser`
 * from the URL and swaps the tagline, hero copy, and welcome toast so
 * the page feels tailored to that user. Auth logic is unchanged — the
 * `login()` call, remember-me, error handling, and post-login /dashboard
 * hop are identical across variants.
 */
const MODES = {
  coach: {
    label: "COACH · SIGN IN",
    hero: "Welcome back, coach.",
    heading: "Get back to your players.",
    subhead: "Pick up where you left off — today's sessions, weekly plans, and player progress waiting inside.",
    ctaLabel: "Sign in to coach",
    welcome: "Welcome back — your players are waiting",
    otherLinks: [
      { to: "/register?role=coach", label: "Apply to coach" },
      { to: "/login?as=player", label: "I'm a player →" },
      { to: "/login?as=organiser", label: "I'm a festival organiser →" },
    ],
  },
  player: {
    label: "PLAYER · SIGN IN",
    hero: "Welcome back on court.",
    heading: "Log in to keep improving.",
    subhead: "Book your next session, track your progress and see what your coach has planned for you.",
    ctaLabel: "Sign in to play",
    welcome: "Welcome back — see what's next on your plan",
    otherLinks: [
      { to: "/register", label: "New here? Sign up" },
      { to: "/login?as=coach", label: "I'm a coach →" },
      { to: "/login?as=organiser", label: "I'm a festival organiser →" },
    ],
  },
  organiser: {
    label: "FESTIVAL ORGANISER · SIGN IN",
    hero: "Welcome back, organiser.",
    heading: "Run your festival.",
    subhead: "Jump straight to your festivals — live courts, wall viewer, self-service and pass purchases in one place.",
    ctaLabel: "Sign in to organise",
    welcome: "Welcome back — your festivals are waiting",
    otherLinks: [
      { to: "/register?as=organiser", label: "New organiser? Sign up" },
      { to: "/login?as=coach", label: "I'm a coach →" },
      { to: "/login?as=player", label: "I'm a player →" },
    ],
  },
};

export default function Login() {
  const [params] = useSearchParams();
  const asParam = params.get("as");
  // Iter467 · Fix 1 — Honour `?next=` so the basket round-trip from the
  // festival landing page returns the signed-in user back to the basket.
  // Falls back to the historical `/dashboard` destination.
  const nextParam = params.get("next");
  const mode = MODES[asParam] || MODES.player;
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const remember = true;
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const { login } = useAuth();
  const nav = useNavigate();

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErr("");
    try {
      await login(email, password, remember);
      toast.success(remember ? `${mode.welcome} — signed in for 30 days` : mode.welcome);
      nav(nextParam || "/dashboard");
    } catch (e2) {
      const status = e2?.response?.status;
      const msg = status === 401
        ? "Incorrect email or password. Please try again."
        : (formatApiError(e2.response?.data?.detail) || "Could not sign in. Please try again.");
      setErr(msg);
      toast.error(msg);
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white grid lg:grid-cols-2 noise-bg">
      <div className="hidden lg:block relative overflow-hidden border-r border-[#262626]">
        <img src="https://images.unsplash.com/photo-1648235692910-947cb90ddd97?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200" alt="Lab" className="absolute inset-0 w-full h-full object-cover grayscale opacity-70" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-transparent to-transparent" />
        <div className="absolute bottom-12 left-12 right-12">
          <div className="label-mono text-[#CCFF00] mb-4">RALLY LAB</div>
          <h2 className="h-display text-5xl tracking-tighter leading-tight" data-testid="login-hero">{mode.hero}</h2>
        </div>
      </div>
      <div className="flex items-center justify-center px-8 py-12">
        <div className="w-full max-w-md">
          <Link to="/" className="flex items-center gap-2 mb-12">
            <div className="w-8 h-8 bg-[#CCFF00] flex items-center justify-center"><Zap strokeWidth={2.5} className="w-5 h-5 text-black" /></div>
            <span className="h-display text-2xl tracking-tighter">RALLY LAB</span>
          </Link>
          <div className="label-mono mb-2" data-testid="login-mode-label">{mode.label}</div>
          <h1 className="h-display text-4xl mb-3 tracking-tighter">{mode.heading}</h1>
          <p className="text-sm text-[#A3A3A3] mb-8">{mode.subhead}</p>
          <form onSubmit={onSubmit} className="space-y-5">
            <div>
              <label className="label-mono block mb-2">Email</label>
              <input data-testid="login-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                className="w-full bg-[#141414] border border-[#262626] px-4 py-3 text-sm focus:outline-none focus:border-[#CCFF00] focus:ring-1 focus:ring-[#CCFF00] transition-colors" />
            </div>
            <div>
              <label className="label-mono block mb-2">Password</label>
              <PasswordInput data-testid="login-password" required value={password} onChange={(e) => setPassword(e.target.value)}
                className="w-full bg-[#141414] border border-[#262626] px-4 py-3 text-sm focus:outline-none focus:border-[#CCFF00] focus:ring-1 focus:ring-[#CCFF00] transition-colors" />
            </div>
            <div className="flex items-center justify-end gap-3">
              <Link to="/forgot-password" data-testid="forgot-password-link" className="text-xs text-[#CCFF00] hover:underline">Forgot?</Link>
            </div>
            {err && (
              <div className="flex items-start gap-2 text-xs text-[#FF4500] border border-[#FF4500]/40 bg-[#FF4500]/5 px-3 py-2" data-testid="login-error"><AlertCircle className="w-4 h-4 shrink-0 mt-[1px]" /><span>{err}</span></div>
            )}
            <button type="submit" disabled={loading} data-testid="login-submit" className="w-full py-3 bg-[#CCFF00] text-black text-sm uppercase tracking-wider font-bold disabled:opacity-40">
              {loading ? "Signing in…" : mode.ctaLabel}
            </button>
          </form>
          <div className="mt-8 pt-6 border-t border-[#141414] text-xs text-[#A3A3A3] space-y-2" data-testid="login-alt-links">
            {mode.otherLinks.map((l) => (
              <div key={l.to}><Link to={l.to} className="hover:text-white">{l.label}</Link></div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
